/* eslint-disable complexity */
import React, {
    useState,
    createContext,
    ReactNode,
    useRef,
    useMemo
} from "react";

import { css, keyframes } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";

import { Picture } from "@/components/wpl/image/src";
import { Video } from "@/components/wpl/video/src";
import { Image as DesignImage } from "@/libraries/design/components/image/src/image";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { LogoBrandV2, Row } from "@/libraries/wpl/foundations/src";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useScrollTracking } from "@/utils/scroll-tracking";

import {
    ProductMarkConfluence,
    ProductMarkJPD,
    ProductMarkJSM,
    ProductMarkJira,
    ProductMarkLoom,
    SquiggleLineAccent1,
    SquiggleLineAccent2,
    ArrowGroupAccent,
    StarGroupAccent,
    LightningAccent1
} from "./illustrations";
import { HomepageSectionScrollItem } from "./scroll-item";

export type ScrollContextType = {
    activeAccentImg: ReactNode | null;
    activeBrand: string | null;
    activeColor: string | null;
    activeProductCTAText: string | null;
    activeProductCTAUrl: string | null;
    activeCustomerCTAUrl: string | null;
    activeFeatureVideo: string | null;
    activePosterImage: string | null;
    activeStaticImage: string | null;
    activeIndex: number | 0;
    activeShape: string | null;
    setActiveAccentImg: (newValue: JSX.Element) => void;
    setActiveBrand: (newValue: string) => void;
    setActiveColor: (newValue: string) => void;
    setActiveProductCTAText: (newValue: string) => void;
    setActiveProductCTAUrl: (newValue: string) => void;
    setActiveCustomerCTAUrl: (newValue: string) => void;
    setActiveFeatureVideo: (newValue: string) => void;
    setActivePosterImage: (newValue: string) => void;
    setActiveStaticImage: (newValue: string) => void;
    setActiveIndex: (newValue: number) => void;
    setActiveShape: React.Dispatch<React.SetStateAction<string | null>>;
};
const ScrollContextState = {
    activeAccentImg: null,
    activeBrand: null,
    activeColor: null,
    activeProductCTAText: null,
    activeProductCTAUrl: null,
    activeCustomerCTAUrl: null,
    activeFeatureVideo: null,
    activePosterImage: null,
    activeStaticImage: null,
    activeIndex: 0,
    activeShape: null,
    setActiveAccentImg: () => undefined,
    setActiveBrand: () => undefined,
    setActiveColor: () => undefined,
    setActiveProductCTAText: () => undefined,
    setActiveProductCTAUrl: () => undefined,
    setActiveCustomerCTAUrl: () => undefined,
    setActiveFeatureVideo: () => undefined,
    setActivePosterImage: () => undefined,
    setActiveStaticImage: () => undefined,
    setActiveIndex: () => 0,
    setActiveShape: () => undefined
};
export const ScrollContext =
    createContext<ScrollContextType>(ScrollContextState);

interface ProductCTAInterface {
    text: string;
    url: string;
    tryCloudUrl: string;
}

export type LegacyTeamType = {
    index?: number;
    productName: string;
    logo: ReactNode;
    logoBGColor?: string;
    productCTA: ProductCTAInterface;
    customerCTAUrl: string;
    header: string;
    description: string;
    quote: string;
    brand: string;
    name: string;
    title: string;
    portrait: string;
    featureVideo: string;
    posterImage: string;
    bgColor: string;
    bgShape: string;
    accentImg: JSX.Element;
};

export type ComprehensiveTeamType = {
    index?: number;
    productName: string;
    logo?: ReactNode;
    productLogoName: LogoBrandV2;
    logoBGColor?: string;
    productCTA: ProductCTAInterface;
    customerCTAUrl: string;
    header: string;
    description: string;
    quote: string;
    brand: string;
    name: string;
    title: string;
    portrait: string;
    featureVideo: string;
    posterImage: string;
    staticImage?: string;
    bgColor: string;
    bgShape: string;
    accentImg: JSX.Element;
    summary: string;
    customerCTA: string;
};

export type TeamType = {
    index?: number;
    productLogoName: LogoBrandV2;
    productCTA: ProductCTAInterface;
    header: string;
    description: string;
    quote: string;
    brand: string;
    name: string;
    title: string;
    portrait: string;
    featureVideo: string;
    posterImage: string;
    staticImage?: string;
    bgColor: string;
    bgShape: string;
    accentImg: JSX.Element;
    customerCTA: string;
    customerCTAUrl: string;
    summary: string;
};

export const ScrollPinnedSections = () => {
    const intl = useIntl();
    const useNewScreenshots = checkFeatureFlagGate(
        featureFlags.gwp9214HomepageScreenshots
    );
    const Teams: ComprehensiveTeamType[] = [
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.ProductName",
                defaultMessage: "Jira"
            }),
            logo: <ProductMarkJira />,
            productLogoName: "jira",
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.Jira.Cta",
                    defaultMessage: "Explore Jira"
                }),
                url: useLocalizedHref("/software/jira"),
                tryCloudUrl: useLocalizedHref(
                    "/try/cloud/signup?bundle=jira-software&edition=free"
                )
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Header",
                defaultMessage: "Dream it, plan it, launch it"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Description-ai",
                defaultMessage:
                    "AI-powered project management to plan, track, and deliver your biggest ideas together."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Quote",
                defaultMessage:
                    "We've been able to reduce our cycle time and increase throughput by 200%, which means we're delivering capabilities to customers faster and increasing customer satisfaction."
            }),
            customerCTAUrl: useLocalizedHref("/customers/lumen"),
            name: "Mai Lian Scarlett",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.JobTitle",
                defaultMessage:
                    "Former Senior Director, Agile Transformation Office, Lumen"
            }),
            summary: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Summary",
                defaultMessage:
                    "Lumen used Jira to reduce cycle time and increase throughput by 200%."
            }),
            customerCTA: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.ReadMore",
                defaultMessage: "Read their story."
            }),
            brand: "https://dam-cdn.atl.orangelogic.com/AssetLink/x8om4p1bytdc7bytrdo15b0q1p8nh7dg.svg",
            portrait:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/a040dn2p4evj6k3041y21an1u5qk4owa.webp",
            featureVideo:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/80qt36g22p4m41p66lxp06m0q3f87abd.mp4",
            posterImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/4j6046mx34c02pmwgtsqa7m5x7bkx16x.webp",
            staticImage: useNewScreenshots
                ? "https://dam-cdn.atl.orangelogic.com/AssetLink/7g6j15a0o21u8b45773ws38rg15ioc73/jira-board-software.webp"
                : undefined,
            accentImg: useMemo(() => <SquiggleLineAccent1 />, []),
            bgColor: wacToken("color.extended.lime500"),
            bgShape:
                "0% 32%, 2% 97%, 56% 100%, 56% 96%, 100% 96%, 100% 0%, 55.5% 2%, 56.5% 18%, 56.5% 18%, 56.5% 18%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.ProductName",
                defaultMessage: "Confluence"
            }),
            logo: <ProductMarkConfluence />,
            productLogoName: "confluence",
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.Confluence.Cta",
                    defaultMessage: "Explore Confluence"
                }),
                url: `${useLocalizedHref("/software/confluence")}`,
                tryCloudUrl: `${useLocalizedHref(
                    "/try/cloud/signup?bundle=confluence&edition=free"
                )}`
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Header",
                defaultMessage: "Partner with AI that knows your business"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Description-ai",
                defaultMessage:
                    "Build and connect plans, documents and ideas with context-aware AI."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Quote",
                defaultMessage:
                    "The big win that we've seen through the implementation of Confluence Cloud is definitely collaboration. It's a very open way of working."
            }),
            customerCTAUrl: useLocalizedHref("/customers/dropbox"),
            name: "Damian Davis",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.JobTitle",
                defaultMessage: "Lead Atlassian Administrator, Dropbox"
            }),
            summary: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Summary",
                defaultMessage:
                    "Dropbox uses Confluence Cloud to create a more open, collaborative way of working."
            }),
            customerCTA: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.ReadMore",
                defaultMessage: "Read their story."
            }),
            brand: "https://dam-cdn.atl.orangelogic.com/AssetLink/rjf5v331x88ie27f5200d1n0e8g84md5.svg",
            portrait:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/q4q14b417k564801vk1p02gbv83r71b1.webp",
            featureVideo:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/nt635rjo7hdklio5nmull65kk44i2wrp.mp4",
            posterImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/b5t3823srnc158mx637784k0271kp4i6.webp",
            staticImage: useNewScreenshots
                ? "https://dam-cdn.atl.orangelogic.com/AssetLink/2wv2e42i053ob2pk68h8524vbi64f634/confluence-scroll.webp"
                : undefined,
            accentImg: useMemo(() => <ArrowGroupAccent />, []),
            bgColor: wacToken("color.extended.purple500"),
            bgShape:
                "0% 1%, 100% 0%, 100% 33%, 55% 33%, 55% 100%, 3% 96%, 2% 32%, 0% 32%, 0% 32%, 0% 32%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.ProductName",
                defaultMessage: "Jira Service Management"
            }),
            logo: <ProductMarkJSM />,
            productLogoName: "jiraservicemanagement",
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.JSM.Cta",
                    defaultMessage: "Explore Jira Service Management"
                }),
                url: `${useLocalizedHref("/software/jira/service-management")}`,
                tryCloudUrl: `${useLocalizedHref(
                    "/try/cloud/signup?bundle=jira-service-management&edition=free"
                )}`
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Header",
                defaultMessage: "Deliver service faster"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Description",
                defaultMessage:
                    "Connect Dev and Ops teams on a single platform with customizable features and AI-powered agents."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Quote",
                defaultMessage:
                    "Atlassian Intelligence acts as a virtual teammate summarizing 5-page PIR reports into 5-sentence recaps for me. It’s already proven its worth by improving productivity across our product teams."
            }),
            customerCTAUrl:
                "https://www.atlassian.com/blog/it-service-management/reimagining-service-management-with-dominos-and-nrma",
            name: "Matthias Hansen",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.JobTitle",
                defaultMessage: "Group CTO, Domino's Pizza Enterprises"
            }),
            summary: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Summary",
                defaultMessage:
                    "Teams at Domino’s Pizza Enterprises Ltd use AI to boost individual and team productivity."
            }),
            customerCTA: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.ReadMore",
                defaultMessage: "See their story."
            }),
            brand: "https://dam-cdn.atl.orangelogic.com/AssetLink/2naan5oor44502o5522ph50clc3ejk8b.svg",
            portrait:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/82ag34s8438g48a538rq5yxrsf2s80o5.webp",
            featureVideo:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/n0q7e6b6ddfs0858muf851j75fvc8643.mp4",
            posterImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/g80pjdq621fklf17b80ikd8gs36rp8g5.webp",
            staticImage: useNewScreenshots
                ? "https://dam-cdn.atl.orangelogic.com/AssetLink/jxc7fgbk61xf52b4h8jvq865i2x1o0h3/jsm-scroll.webp"
                : undefined,
            accentImg: useMemo(() => <LightningAccent1 />, []),
            bgColor: wacToken("color.extended.yellow400"),
            bgShape:
                "0% 41%, 8% 41%, 10% 21%, 65% 13%, 67% 3%, 100% 0%, 98% 27%, 100% 27%, 100% 100%, 0% 98%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.ProductName",
                defaultMessage: "Loom"
            }),
            logo: <ProductMarkLoom />,
            productLogoName: "loom",
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.Loom.Cta",
                    defaultMessage: "Explore Loom"
                }),
                url: useLocalizedHref("/software/loom"),
                tryCloudUrl: useLocalizedHref(
                    "/try/cloud/signup?bundle=loom&edition=premium&sourceComponent=loom-direct"
                )
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Header",
                defaultMessage: "Record, share, collaborate"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Description",
                defaultMessage:
                    "Easily record and share AI-powered video messages to reach a broader audience for better async collaboration."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Quote",
                defaultMessage:
                    "Loom has been the light of my life since you showed me it. I never tire of hearing this from folks."
            }),
            customerCTAUrl: useLocalizedHref("/customers"),
            name: "Alexis Ohanian",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.JobTitle",
                defaultMessage: "Founder, Seven Seven Six"
            }),
            summary: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Summary",
                defaultMessage:
                    "Loom has been the light of my life since you showed me it. I never tire of hearing this from folks."
            }),
            customerCTA: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.ReadMore",
                defaultMessage: "Read their story."
            }),
            brand: "https://dam-cdn.atl.orangelogic.com/AssetLink/215050ci4w203s0mrd76eb2kf0o73rd6.svg",
            portrait:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/si3h814nhij7mqksea3r3bq22kspnqmg.webp",
            featureVideo:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/27ujcvoyd73u73c33d00a5q4gyqd40n0.mp4",
            posterImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/ok3kd81lm1e452l8rc5gqagc0g72353m.webp",
            staticImage: useNewScreenshots
                ? "https://dam-cdn.atl.orangelogic.com/AssetLink/kvro7aryebr3501e4sw2n4kj2536vw4a/loop-scroll.webp"
                : undefined,
            accentImg: useMemo(() => <SquiggleLineAccent2 />, []),
            bgColor: wacToken("color.primary.blue"),
            bgShape:
                "0% 1%, 30% 0%, 30% 3%, 100% 0%, 99% 99%, 1% 100%, 1% 100%, 1% 100%, 1% 100%, 1% 100%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.ProductName",
                defaultMessage: "Jira Product Discovery"
            }),
            logo: <ProductMarkJPD />,
            productLogoName: "jiraproductdiscovery",
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.JPD.Cta",
                    defaultMessage: "Explore Jira Product Discovery"
                }),
                url: `${useLocalizedHref("/software/jira/product-discovery")}`,
                tryCloudUrl: `${useLocalizedHref(
                    "/try/cloud/signup?bundle=jira-product-discovery"
                )}`
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Header",
                defaultMessage: "Ideate faster than ever"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Description",
                defaultMessage:
                    "Capture and prioritize ideas and align everyone with product roadmaps - all in one single Jira platform."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Quote",
                defaultMessage:
                    "Jira Product Discovery translated our entire process into a highly transparent workflow, connected to the work we were already doing in Jira. That was a mind-blowing moment."
            }),
            customerCTAUrl: useLocalizedHref("/customers/doodle"),
            name: "Stephanie Leue",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.JobTitle",
                defaultMessage: "Former Chief Product Officer, Doodle"
            }),
            summary: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Summary",
                defaultMessage:
                    "Doodle uses Jira Product Discovery to translate their entire process into a highly transparent workflow, connected to the work they were already doing in Jira."
            }),
            customerCTA: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.ReadMore",
                defaultMessage: "Read their story."
            }),
            brand: "https://dam-cdn.atl.orangelogic.com/AssetLink/30ur058vt123kkh4mp68ivv6qopl0bd3.svg",
            portrait:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/5ojgmwt84ty0yr7426611y50t3spsyt8.webp",
            featureVideo:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/mk2h13861hj08rhewrfn05bpn87s2730.mp4",
            posterImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/83tq3402yb1w28fv8wt70804613ev0is.webp",
            staticImage: useNewScreenshots
                ? "https://dam-cdn.atl.orangelogic.com/AssetLink/03c6d0erv445pgc061s8l422v5b36457/jpd-scroll.webp"
                : undefined,
            accentImg: useMemo(() => <StarGroupAccent />, []),
            bgColor: wacToken("color.extended.lime500"),
            bgShape:
                "0% 20%, 48% 10%, 48% 2%, 100% 0%, 100% 97%, 55% 100%, 55% 96%, 2% 95%, 2% 95%, 2% 95%"
        }
    ];
    const imgSectionRef = useRef<HTMLDivElement>(null);

    const defaultFeatureVideo: string = Teams[0]?.featureVideo
        ? Teams[0]?.featureVideo
        : "";
    const defaultFeaturePosterImage: string = Teams[0]?.posterImage
        ? Teams[0]?.posterImage
        : "";
    const defaultStaticImage: string = Teams[0]?.staticImage
        ? Teams[0]?.staticImage
        : "";
    const defaultProductCTAText: string = Teams[0]?.productCTA.text
        ? Teams[0]?.productCTA.text
        : "";
    const defaultProductCTAUrl: string = Teams[0]?.productCTA.url
        ? Teams[0]?.productCTA.url
        : "";
    const defaultBgShape: string = Teams[0]?.bgShape ? Teams[0]?.bgShape : "";
    const defaultBgColor: string = Teams[0]?.bgColor ? Teams[0]?.bgColor : "";

    const { ref: scrollRef, inView: sectionIsVisible } = useInView({
        threshold: 0.15
    });
    const { ref: headerScrollRef, inView: headerIsVisible } = useInView({
        threshold: 1
    });
    const [activeAccentImg, setActiveAccentImg] = useState<JSX.Element>();
    const [activeBrand, setActiveBrand] = useState<string>("");
    const [activeColor, setActiveColor] = useState<string>(defaultBgColor);
    const [activeProductCTAText, setActiveProductCTAText] = useState<string>(
        defaultProductCTAText
    );
    const [activeProductCTAUrl, setActiveProductCTAUrl] =
        useState<string>(defaultProductCTAUrl);
    const [activeCustomerCTAUrl, setActiveCustomerCTAUrl] =
        useState<string>("");
    const [activeFeatureVideo, setActiveFeatureVideo] =
        useState<string>(defaultFeatureVideo);
    const [activePosterImage, setActivePosterImage] = useState<string>(
        defaultFeaturePosterImage
    );
    const [activeStaticImage, setActiveStaticImage] =
        useState<string>(defaultStaticImage);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [activeShape, setActiveShape] = useState<string | null>(
        defaultBgShape
    );
    const { ref: scrollTrackingRef } = useScrollTracking(
        "ScrollPinnedSections"
    );

    return (
        <section>
            <ScrollContext.Provider
                value={{
                    activeAccentImg,
                    activeBrand,
                    activeColor,
                    activeProductCTAText,
                    activeProductCTAUrl,
                    activeCustomerCTAUrl,
                    activeFeatureVideo,
                    activePosterImage,
                    activeStaticImage,
                    activeIndex,
                    activeShape,
                    setActiveAccentImg,
                    setActiveBrand,
                    setActiveColor,
                    setActiveFeatureVideo,
                    setActivePosterImage,
                    setActiveStaticImage,
                    setActiveIndex,
                    setActiveShape,
                    setActiveProductCTAText,
                    setActiveProductCTAUrl,
                    setActiveCustomerCTAUrl
                }}
            >
                <UFOSegment name="homepage-v2-scrollPinnedSection">
                    <div
                        css={[scrollerStyles]}
                        ref={scrollRef}
                        className={sectionIsVisible ? "is-visible" : "hidden"}
                    >
                        <Row>
                            <div className="row-width" css={rowWidthStyles}>
                                <div
                                    css={headerWrapperStyles}
                                    className="header-wrapper"
                                    ref={scrollTrackingRef}
                                >
                                    <div
                                        css={css({
                                            insetBlockStart: "0",
                                            display: "flex",
                                            flexDirection: "column"
                                        })}
                                    >
                                        <div
                                            css={accentHeadingStyles}
                                            className="accent-heading"
                                        >
                                            <h2
                                                css={[h2Styles]}
                                                ref={headerScrollRef}
                                                className={
                                                    headerIsVisible
                                                        ? "is-visible"
                                                        : "hidden"
                                                }
                                            >
                                                <div>
                                                    <Picture
                                                        css={[
                                                            sparklesImgStyles,
                                                            sparklesImg1Styles,
                                                            headerIsVisible &&
                                                                sparklesImg1AnimationStyles
                                                        ]}
                                                        image={{
                                                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/017a6562kool0414o36wc5ll0ijk3787.svg",
                                                            alt: "",
                                                            width: 19,
                                                            height: 19
                                                        }}
                                                    />
                                                    <Picture
                                                        css={[
                                                            sparklesImgStyles,
                                                            sparklesImg2Styles,
                                                            headerIsVisible &&
                                                                sparklesImg2AnimationStyles
                                                        ]}
                                                        image={{
                                                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1wj34u7l84d64u01g38tn8ta0j4788mf.svg",
                                                            alt: "",
                                                            width: 20,
                                                            height: 20
                                                        }}
                                                    />
                                                    <Picture
                                                        css={[
                                                            sparklesImgStyles,
                                                            sparklesImg3Styles,
                                                            headerIsVisible &&
                                                                sparklesImg3AnimationStyles
                                                        ]}
                                                        image={{
                                                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/y88c71e0n8m74s1hby47odn7b765b46q.svg",
                                                            alt: "",
                                                            width: 37,
                                                            height: 36
                                                        }}
                                                    />
                                                </div>
                                                {intl.formatMessage({
                                                    id: "HomePage.ScrollPinnedSectionHeader",
                                                    defaultMessage:
                                                        "Teamwork solutions for high-performing teams"
                                                })}
                                            </h2>
                                        </div>
                                        <div
                                            ref={imgSectionRef}
                                            className="half img-section"
                                            css={imgSectionStyles}
                                        >
                                            <div
                                                css={imgRegionStyles}
                                                className="img-region"
                                            >
                                                <div
                                                    css={[
                                                        bgShapeStyles,
                                                        activeIndex % 2 == 0 &&
                                                            evenBgShapeAnimation,
                                                        activeIndex % 2 !== 0 &&
                                                            oddBgShapeAnimation
                                                    ]}
                                                    className="bg-shape"
                                                    style={{
                                                        clipPath: `polygon(${activeShape})`,
                                                        backgroundColor:
                                                            activeColor
                                                    }}
                                                />
                                                <div
                                                    css={[
                                                        imgAccentWrapperStyles
                                                    ]}
                                                    className="img-accent"
                                                >
                                                    {activeAccentImg}
                                                </div>
                                                {activeStaticImage ? (
                                                    <DesignImage
                                                        style={{
                                                            width: "100%",
                                                            height: "auto",
                                                            aspectRatio:
                                                                1536 / 800,
                                                            position: "relative"
                                                        }}
                                                        css={
                                                            imgScreenshotStyles
                                                        }
                                                        className="img-screenshot"
                                                        src={activeStaticImage}
                                                        alt=""
                                                        width={1536}
                                                        height={800}
                                                    />
                                                ) : (
                                                    <Video
                                                        css={
                                                            imgScreenshotStyles
                                                        }
                                                        videoLink={
                                                            activeFeatureVideo
                                                        }
                                                        videoType="mp4"
                                                        className="img-screenshot"
                                                        videoPreviewImage={
                                                            activePosterImage
                                                        }
                                                        aspectRatio="16/9"
                                                        loop={false}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    css={textSectionStyles}
                                    className="half text-section"
                                >
                                    {Teams.map(
                                        (
                                            team: ComprehensiveTeamType,
                                            index: number
                                        ) => (
                                            <HomepageSectionScrollItem
                                                key={index}
                                                index={index}
                                                brand={team.brand}
                                                quote={team.quote}
                                                productLogoName={
                                                    team.productLogoName
                                                }
                                                productCTA={team.productCTA}
                                                header={team.header}
                                                description={team.description}
                                                name={team.name}
                                                title={team.title}
                                                portrait={team.portrait}
                                                summary={team.summary}
                                                customerCTA={team.customerCTA}
                                                customerCTAUrl={
                                                    team.customerCTAUrl
                                                }
                                                featureVideo={team.featureVideo}
                                                posterImage={team.posterImage}
                                                staticImage={team.staticImage}
                                                bgColor={team.bgColor}
                                                bgShape={team.bgShape}
                                                accentImg={team.accentImg}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </Row>
                    </div>
                </UFOSegment>
            </ScrollContext.Provider>
        </section>
    );
};

const fadeIn = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});

const bgShapePulseOdd = keyframes({
    "0%": {
        transform: "scale(1)"
    },
    "50%": {
        transform: "scale(0.9)"
    },
    "100%": {
        transform: "scale(1)"
    }
});

const bgShapePulseEven = keyframes({
    "0%": {
        transform: "scale(1)"
    },
    "50%": {
        // .1 added here to prevent even and odd styles from being combined into one
        transform: "scale(0.91)"
    },
    "100%": {
        transform: "scale(1)"
    }
});

const rowWidthStyles = css({
    position: "relative",
    [media.below.md]: {
        flexDirection: "column",
        marginBlockEnd: "-40px"
    }
});

const imgSectionStyles = css({
    alignItems: "flex-end",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flexBasis: "60%",
    position: "sticky",
    insetBlockStart: "0",
    paddingBlockStart: "20px",
    width: "calc(58% - 15px)",
    alignSelf: "flex-end",
    minHeight: 0,

    [media.below.md]: {
        display: "none"
    }
});

const textSectionStyles = css({
    [media.above.md]: {
        width: "calc(42% - 15px)",
        position: "relative",
        insetBlockStart: "-240px",
        marginBlockStart: "calc(15% - 116px)",
        marginBlockEnd: "calc(19% - 116px)"
    }
});

const headerWrapperStyles = css({
    [media.above.md]: {
        position: "sticky",
        insetBlockStart: "0",
        zIndex: "2",
        clipPath: `polygon(0 0,100% 0,100% 100%,33% 100%,33% 37%,0 37%)`
    }
});

const accentHeadingStyles = css({
    background: `linear-gradient(180deg, var(--background-color-1) 90%, ${wacToken(
        "color.neutral.light0"
    )})`,
    paddingBlockStart: "7vh",
    paddingBlockEnd: "20px"
});

const h2Styles = css({
    position: "relative",
    textAlign: "left",

    [media.below.md]: {
        textAlign: "center"
    }
});

const sparklesImgStyles = css({
    position: "absolute",
    opacity: 0
});

const sparklesImg1Styles = css({
    insetInlineStart: "63.2%",
    insetBlockStart: "-75px"
});
const sparklesImg2Styles = css({
    insetInlineStart: "60%",
    insetBlockStart: "-52px"
});
const sparklesImg3Styles = css({
    insetInlineStart: "62.7%",
    insetBlockStart: "-42px"
});

const sparklesImg1AnimationStyles = css({
    animation: `${fadeIn} ease-out .2s 1 .2s forwards`
});
const sparklesImg2AnimationStyles = css({
    animation: `${fadeIn} ease-out .2s 1 .4s forwards`
});
const sparklesImg3AnimationStyles = css({
    animation: `${fadeIn} ease-out .2s 1 .6s forwards`
});

const imgRegionStyles = css({
    position: "relative",
    marginBlockEnd: "40px",
    aspectRatio: "7/4",
    padding: "35px",
    width: "100%"
});

const bgShapeStyles = css({
    width: "100%",
    height: "100%",
    transition: "all 1s",
    position: "absolute",
    insetInlineStart: 0,
    insetBlockStart: 0
});

// dynamic bg styles
const oddBgShapeAnimation = css({
    animation: `${bgShapePulseOdd} ease-in-out 1s 1`
});
const evenBgShapeAnimation = css({
    animation: `${bgShapePulseEven} ease-in-out 1s 1`
});

const imgAccentWrapperStyles = css({
    width: "20%",
    position: "absolute",
    insetInlineStart: "-3%",
    insetBlockEnd: "-10%"
});

const imgScreenshotStyles = css({
    position: "relative",
    display: "block",
    height: "auto",
    width: "100%",
    margin: "auto",
    animation: `${fadeIn} 0.25s 1`,
    borderRadius: "5px",
    border: `3px solid ${wacToken("color.neutral.light200")}`
});

const scrollerStyles = css({
    "@property --background-color-1": {
        syntax: "'<color>'",
        initialValue: wacToken("color.neutral.light0"),
        inherits: "false"
    },
    "--background-color-1": wacToken("color.neutral.light0"),
    "transition": "background-color 1s",
    "position": "relative",
    "margin": "20px 0 115px",
    "backgroundColor": "var(--background-color-1)",
    "backgroundImage":
        "linear-gradient(to bottom, white 40px, transparent 50px), linear-gradient(to top, white 100px, transparent 200px)",

    [media.below.md]: {
        margin: "20px 0 50px"
    },

    [media.above.md]: {
        // nesting used here because un-nesting is too complicated and requires a major refactor
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".team-type": {
            "&:first-of-type": {
                marginBlockStart: "-45%"
            },
            "&:last-child": {
                marginBlockEnd: "-45%"
            }
        }
    }
});
